import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Button, Typography } from 'lighthouse';

import Counter from '@/widgets/Counter';
import LoginModal from '@/widgets/LoginModal';

import { isUserLogin } from '@/utilities/CheckUserIsLogin';
import { resolveFulfillmentServiceType, resolveOfferFulfillmentProfiles } from '@/utilities/fulfillment';

import { useCartStore } from '@/stores/cart/useCartStore';
import { useStoreProductsModalStore } from '@/stores/storeProductsModal/useStoreProductsModalStore';

import { checkoutCartPage } from '@/constants/routes';

import { AddToCartButtonProps } from './models';

import { FulfillmentProfile } from '@/server/services/vendor/models';
import { LocationTick } from 'iconsax-react';

const AddToCartButton = ({
    available,
    isPriceLoading,
    children,
    href,
    isMainVendor,
    allStores,
    winner,
    onOpenVendorsModal,
    selectedFulfillmentProfile,
    ...props
}: AddToCartButtonProps) => {
    const buttonVariant = props.variant || 'contained';
    const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
    const [quantity, setQuantity] = useState(0);
    const { addOfferToCart, updateCartItem, deleteCartItem, cart, isUpdateCartItemLoading, UpdateCartItemId } = useCartStore((state) => state);

    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const availableDeliveryMethods = useMemo(() => {
        const fulfillmentProfiles = resolveOfferFulfillmentProfiles(winner).filter((profile) => profile.isEligible === true);
        if (!fulfillmentProfiles.length) return [];

        return fulfillmentProfiles.map((profile) => {
            const serviceType = resolveFulfillmentServiceType(profile.fulfillmentType);

            return {
                id: profile.fulfillmentProfileId,
                label: serviceType.label,
                description: serviceType.description,
                icon: serviceType.icon,
                fulfillmentProfile: profile
            };
        });
    }, [winner]);

    const cartItemsByFulfillmentProfileId = useMemo(() => {
        const items = cart?.items?.filter((item) => item.offerId === winner.offerId) ?? [];

        return new Map(items.map((item) => [item.fulfillmentProfileId, item]));
    }, [cart?.items, winner.offerId]);
    const hasCartItem = cartItemsByFulfillmentProfileId.size > 0;
    const defaultDeliveryMethod =
        availableDeliveryMethods.find((item) => !cartItemsByFulfillmentProfileId.has(String(item.fulfillmentProfile.fulfillmentProfileId))) ??
        availableDeliveryMethods[0] ??
        null;

    const resolveDeliveryMethod = useMemo(() => {
        if (selectedFulfillmentProfile) {
            const matched = availableDeliveryMethods.find((item) => item.id === selectedFulfillmentProfile.fulfillmentProfileId);
            if (matched) return matched;
        }
        return defaultDeliveryMethod;
    }, [selectedFulfillmentProfile, availableDeliveryMethods, defaultDeliveryMethod]);

    const [deliveryValue, setDeliveryValue] = useState(resolveDeliveryMethod);
    const offerCartItem = cart?.items?.find((item) => item.offerId === winner.offerId);
    const remainingQuantity = offerCartItem?.availableQuantity ?? winner.availableQuantity;
    const isDisabled = !available || isPriceLoading || (!isMainVendor && (remainingQuantity <= 0 || !deliveryValue));

    const currentCartItem = deliveryValue
        ? (cartItemsByFulfillmentProfileId.get(String(deliveryValue.fulfillmentProfile.fulfillmentProfileId)) ?? null)
        : null;
    const isCurrentItemInCart = !!currentCartItem;
    const isCounterLoading = isUpdateCartItemLoading && currentCartItem?.itemId === UpdateCartItemId;
    const buttonText = !available && !isPriceLoading ? 'ناموجود' : hasCartItem && !isCurrentItemInCart ? 'افزودن با روش دیگر' : 'افزودن به سبد خرید';
    const showCounter = isCurrentItemInCart && !isMainVendor;

    useEffect(() => {
        setDeliveryValue(resolveDeliveryMethod);
    }, [resolveDeliveryMethod]);

    useEffect(() => {
        if (!deliveryValue || !availableDeliveryMethods.find((item) => item.id === deliveryValue.id)) {
            setDeliveryValue(defaultDeliveryMethod);
        }
    }, [availableDeliveryMethods, defaultDeliveryMethod, deliveryValue]);

    useEffect(() => {
        if (currentCartItem) {
            setQuantity(currentCartItem.quantity);
        }
    }, [currentCartItem?.quantity, currentCartItem?.itemId]);

    const router = useRouter();

    const handleAddToCartProduct = () => {
        if (isMainVendor && href) {
            router.push(href);
        } else {
            if (!isUserLogin()) {
                setIsLoginModalOpen(true);
                return;
            }

            void handleAddToCart();
        }
    };

    const handleUserLogin = () => {
        setIsLoginModalOpen(false);
        void handleAddToCart();
    };

    const handleAddToCart = async () => {
        if (!winner.offerId || !winner.storeUuid || isDisabled || deliveryValue?.fulfillmentProfile.isEligible !== true) return;

        setIsAddToCartLoading(true);
        try {
            const selectedFulfillmentProfile: FulfillmentProfile | undefined = deliveryValue?.fulfillmentProfile;

            if (!selectedFulfillmentProfile) return;

            const existingItem = cartItemsByFulfillmentProfileId.get(String(selectedFulfillmentProfile.fulfillmentProfileId));

            if (existingItem && cart) {
                await updateCartItem({
                    cartId: cart.cartId,
                    id: existingItem.itemId,
                    currentQuantity: existingItem.quantity,
                    offerId: winner.offerId,
                    quantity: existingItem.quantity + 1
                });
            } else {
                await addOfferToCart(
                    {
                        offerId: winner.offerId,
                        quantity: 1,
                        fulfillmentProfileId: selectedFulfillmentProfile.fulfillmentProfileId,
                        fulfillmentType: selectedFulfillmentProfile.fulfillmentType
                    },
                    winner.storeName ?? '',
                    winner.productTitle,
                    winner.storeUuid
                );

                useStoreProductsModalStore.getState().open({
                    storeUuid: winner.storeUuid,
                    storeName: winner.storeName ?? '',
                    excludedProductUuid: winner.productUuid
                });
            }
        } catch {
        } finally {
            setIsAddToCartLoading(false);
        }
    };

    const handleCounterChange = async (newQuantity: number) => {
        if (!currentCartItem || !cart || isCounterLoading || newQuantity === quantity || !winner.offerId) {
            return;
        }

        const prevQuantity = quantity;
        setQuantity(newQuantity);

        try {
            const queryParam = {
                cartId: cart.cartId,
                id: currentCartItem.itemId,
                offerId: winner.offerId,
                quantity: newQuantity,
                currentQuantity: prevQuantity
            };

            if (prevQuantity === 1 && newQuantity === 0) {
                await deleteCartItem(queryParam);
            } else {
                await updateCartItem(queryParam);
            }
        } catch {
            setQuantity(prevQuantity);
        }
    };

    const renderCartAction = (isSticky = false) => {
        if (showCounter && currentCartItem) {
            if (isSticky) {
                return (
                    <div className='h-full w-6/12 max-w-fit'>
                        <Counter
                            value={quantity}
                            max={quantity + Math.max(0, remainingQuantity)}
                            onChange={handleCounterChange}
                            isLoading={isCounterLoading}
                            disabled={isCounterLoading || isPriceLoading}
                        />
                    </div>
                );
            }

            return (
                <div
                    className='flex w-full items-center justify-end lg:min-h-12 lg:justify-between lg:gap-4 lg:rounded-lg lg:border lg:border-solid
                        lg:border-primary/20 lg:bg-primary/[0.04] lg:px-4 lg:py-3'>
                    <div className='hidden min-w-0 flex-1 lg:block'>
                        <Typography variant='body2' fontWeight={600} color='blackHighEmphasis'>
                            در سبد شما
                        </Typography>
                        <Link href={checkoutCartPage} className='mt-0.5 inline-flex items-center gap-1 transition-opacity hover:opacity-80'>
                            <Typography variant='caption' color='blackMediumEmphasis'>
                                مشاهده
                            </Typography>
                            <Typography variant='caption' fontWeight={700} className='text-primary'>
                                سبد خرید
                            </Typography>
                        </Link>
                    </div>

                    <Counter
                        value={quantity}
                        max={quantity + Math.max(0, remainingQuantity)}
                        onChange={handleCounterChange}
                        isLoading={isCounterLoading}
                        disabled={isCounterLoading || isPriceLoading}
                    />
                </div>
            );
        }

        return (
            <Button
                {...props}
                variant={buttonVariant}
                onClick={handleAddToCartProduct}
                size={props.size ?? (isSticky ? 'large' : undefined)}
                className={isSticky ? 'h-full w-6/12 max-w-fit' : `w-full ${buttonVariant === 'contained' ? 'text-white' : ''}`}
                disabled={isDisabled}
                loading={isAddToCartLoading || isPriceLoading}>
                {isMainVendor && href ? 'انتخاب' : buttonText}
            </Button>
        );
    };

    return (
        <div>
            {children && href ? (
                <div
                    className='position-dynamic bottom-0 right-0 z-10 w-full rounded-t-lg border border-solid border-gray-200 bg-gray-50 p-1 pb-0
                        shadow-md transition-all duration-300'>
                    <div className={'flex h-full w-full items-center justify-between rounded-lg border border-solid border-gray-100 bg-white p-3'}>
                        {renderCartAction(true)}

                        {children}
                    </div>

                    <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-2'>
                        <div className='flex items-center gap-2 text-[10px] text-gray-600'>
                            <div className='flex items-start gap-1.5'>
                                <LocationTick color='currentcolor' className='mt-0.5 h-3 w-3 text-blackLowEmphasis' />
                                <div className='flex items-center gap-1'>
                                    <span className='text-xs font-semibold text-blackMediumEmphasis'>فروشنده :</span>
                                    <span className='text-xs text-primary'>{winner ? winner.storeName : 'ازکی‌سرویس'}</span>
                                </div>
                            </div>
                        </div>

                        {allStores.length > 1 && (
                            <Button onClick={onOpenVendorsModal} size='xSmall' className=''>
                                تغییر فروشنده
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                renderCartAction()
            )}

            {isLoginModalOpen && <LoginModal onUserLogin={handleUserLogin} onClose={() => setIsLoginModalOpen(false)} />}
        </div>
    );
};

export default AddToCartButton;
